<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useApiClient } from "@/api/client";
import { useExplorerStore } from "@/modules/explorer/store";
import { processedDocument } from "chartjs-graph";
import DocumentDO = App.Data.Explorer.DocumentDO;
import ExplorerAiSettingsDO = App.Data.ExplorerAiSettingsDO;
const explorerStore = useExplorerStore();
const document = computed<DocumentDO>(() => explorerStore.document);

const aiResponse = ref("");
const isLoading = ref(false);

const props = defineProps({
	modelValue: {
		type: Boolean,
		required: true,
	},
});

const emit = defineEmits(["update:modelValue"]);

const drawer = computed({
	get: () => props.modelValue,
	set: (value) => emit("update:modelValue", value),
});

const languageItems = [
	{
		title: "English",
		description: "Responses will be generated in English",
	},
	{
		title: "Dutch",
		description: "Antwoorden worden in het Nederlands gegenereerd",
	},
];

const historyItems = [
	{
		title: "Previous Week",
		description: "The AI will take the previous week as context",
	},
	{
		title: "Previous Month",
		description: "The AI will take the previous month as context",
	},
	{
		title: "Previous Year",
		description: "The AI will take the previous year as context",
	},
];

const defaultSettings: ExplorerAiSettingsDO = {
	language: "Dutch",
	history_context: "Previous Year",
};

const localSettings = reactive<ExplorerAiSettingsDO>(defaultSettings);
const generateExplorerRapport = async () => {
	isLoading.value = true;
	aiResponse.value = "";
	await useApiClient("aiReport").generateExplorerRapport(
		processedDocument(document.value),
		localSettings,
		(token) => {
			aiResponse.value += token;
		},
		() => {
			return;
		}
	);

	isLoading.value = false;
};
</script>

<template>
	<v-navigation-drawer
		v-model="drawer"
		location="right"
		temporary
		scrim="transparent"
		class="card-drawer"
		width="500"
	>
		<v-card class="pa-0 ma-0 h-100 d-flex flex-column">
			<v-toolbar color="white" class="flex-shrink-0">
				<v-toolbar-title class="w-100">
					<i class="fa-duotone fa-brain mr-3"></i>
					AI Context
				</v-toolbar-title>

				<v-spacer />

				<v-btn icon @click="drawer = false">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>
			<v-card class="pa-3 flex-shrink-0" elevation="1" rounded="lg">
				<v-select
					v-model="localSettings.language"
					label="Output Language"
					:items="languageItems"
					density="compact"
					variant="outlined"
					hide-details
					class="mb-2 pb-2"
				>
					<template #item="{ props, item }">
						<v-list-item v-bind="props">
							<v-list-item-subtitle class="text-caption">
								{{ item.raw.description }}
							</v-list-item-subtitle>
						</v-list-item>
					</template>
				</v-select>
				<v-select
					v-model="localSettings.history_context"
					label="History"
					:items="historyItems"
					density="compact"
					variant="outlined"
					hide-details
				>
					<template #item="{ props, item }">
						<v-list-item v-bind="props">
							<v-list-item-subtitle class="text-caption">
								{{ item.raw.description }}
							</v-list-item-subtitle>
						</v-list-item>
					</template>
				</v-select>
			</v-card>

			<v-divider class="flex-shrink-0" />

			<div v-if=!isLoading class="flex-shrink-0 pa-2">
				<v-btn
					color="primary"
					size="large"
					block
					:disabled="isLoading"
					@click="generateExplorerRapport"
				>
					<span v-if="!isLoading">Generate Report</span>
				</v-btn>
			</div>
			<div style="flex: 1 1 0; min-height: 0; overflow-y: auto">
				<v-skeleton-loader
					v-if="isLoading"
					class="mx-auto"
					type="heading, paragraph, paragraph, paragraph, heading, paragraph"
				/>
				<v-card v-if="aiResponse" class="pa-4 ma-2" outlined elevation="1">
					<div class="ai-report-text" v-html="aiResponse"></div>
				</v-card>
			</div>
		</v-card>
	</v-navigation-drawer>
</template>

<style scoped lang="scss">
.ai-report-text {
	white-space: pre-wrap;
	overflow-wrap: anywhere;
	line-height: 1.6;
	font-size: 0.95rem;
	color: rgba(0, 0, 0, 0.85);
}
</style>
